// What a Bulk Shell run targets: the Choose devices window's choice, its
// preview / create request, and the line the drawer shows for it. Pure, so
// `npm test` runs it under node --test (relative imports carry `.ts`).
import { parseDeviceList } from './bulk-shell-prefill.ts';
import { LAST_SEEN_OPTIONS, compactFacetFilter, hasFacetFilter } from './device-filter-options.ts';
import type { ShellJobCheckLine, ShellJobDeviceFilter, ShellJobPreview, ShellJobTarget, ShellJobTargetRequest } from './shell-job.service';
import { displayDeviceType } from './utils.ts';

export interface DevicesChoice {
  mode: ShellJobTarget;
  filter: ShellJobDeviceFilter;
  /** Paste list: the text as typed. */
  listText: string;
  /** Group: the software update group's id. */
  groupId: string;
}

/** The preview / create request for a choice; null while a pasted list or a group is empty. */
export function targetRequest(c: DevicesChoice, tenant: Pick<ShellJobTargetRequest, 'tenant_id'>): ShellJobTargetRequest | null {
  if (c.mode === 'list') {
    const devices = parseDeviceList(c.listText);
    return devices.length > 0 ? { ...tenant, target: 'list', devices } : null;
  }
  if (c.mode === 'group') return c.groupId ? { ...tenant, target: 'group', group_id: c.groupId } : null;
  if (c.mode === 'filter') {
    const f = compactFacetFilter(c.filter);
    if (hasFacetFilter(f)) return { ...tenant, target: 'filter', filter: f };
  }
  return { ...tenant, target: 'all' };
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Type: Rower · Status: Online": a filter as its chips read. */
export function facetSummary(f: ShellJobDeviceFilter): string {
  const parts: string[] = [];
  if (f.type?.length) parts.push(`Type: ${[...new Set(f.type.map((t) => cap(displayDeviceType(t))))].join(', ')}`);
  if (f.status) parts.push(`Status: ${cap(f.status)}`);
  if (f.model?.length) parts.push(`Model: ${f.model.join(', ')}`);
  if (f.agent_version?.length) parts.push(`Agent version: ${f.agent_version.join(', ')}`);
  if (f.os_version?.length) parts.push(`OS version: ${f.os_version.join(', ')}`);
  if (f.rom_version?.length) parts.push(`ROM: ${f.rom_version.join(', ')}`);
  if (f.is_internal !== undefined) parts.push(`Ownership: ${f.is_internal ? 'Internal' : 'Customer'}`);
  if (f.last_seen) parts.push(`Activity: ${LAST_SEEN_OPTIONS.find((o) => o.value === f.last_seen)?.label ?? f.last_seen}`);
  if (f.sub_type?.length) parts.push(`Sub-type: ${f.sub_type.join(', ')}`);
  if (f.search) parts.push(`Search: ${f.search}`);
  return parts.join(' · ');
}

/** The line under the drawer's Devices summary (spec §9.1). */
export function choiceLine(c: DevicesChoice, ctx: { tenantName?: string; groupName?: string; lines?: ShellJobCheckLine[] }): string {
  const all = `All devices in ${ctx.tenantName ?? 'this tenant'}`;
  if (c.mode === 'filter') return facetSummary(c.filter) || all;
  if (c.mode === 'group') return `Group · ${ctx.groupName ?? 'deleted group'}`;
  if (c.mode === 'list') {
    const lines = ctx.lines ?? [];
    const notUsed = lines.filter((l) => l.result !== 'matched').length;
    return `Pasted list · ${lines.length} machine IDs${notUsed ? `, ${notUsed} not used` : ''}`;
  }
  return all;
}

/** A pasted list's last check: the machine IDs it covered and what the server said. */
export interface ListCheck {
  devices: string[];
  preview: ShellJobPreview;
}

/** Whether the pasted list is still the one its last check covered; spacing, separators and order do not matter. */
export function listCheckFresh(listText: string, checked: string[] | undefined): boolean {
  if (!checked) return false;
  const now = parseDeviceList(listText);
  const was = new Set(checked);
  return now.length > 0 && now.length === was.size && now.every((d) => was.has(d));
}

/** The check without one unmatched line: such a line never counted, so the totals still hold. */
export function dropCheckedLine(check: ListCheck, input: string): ListCheck {
  return {
    devices: check.devices.filter((d) => d !== input),
    preview: {
      ...check.preview,
      unknown: check.preview.unknown.filter((u) => u !== input),
      lines: check.preview.lines?.filter((l) => l.input !== input),
    },
  };
}
